import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import SafeIcon from '../common/SafeIcon';

const ArtworkModal = ({ artwork, onClose }) => {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  
  if (!artwork) return null;

  const title = lang === 'en' ? artwork.titleEn || artwork.title : artwork.title;
  const description = lang === 'en' ? artwork.description : artwork.descriptionEs || artwork.description;

  const handleAction = () => {
    if (artwork.stripe_payment_link) {
      window.open(artwork.stripe_payment_link, '_blank');
    } else {
      navigate('/contact', { state: { artworkTitle: title } });
      onClose();
    }
  };

  return (
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-8 bg-gallery-bg/95 backdrop-blur-md"
      >
        <button 
          onClick={onClose}
          className="absolute top-6 right-6 p-2 text-gallery-text hover:text-gallery-accent transition-colors z-[70]"
        >
          <SafeIcon name="X" className="w-8 h-8" />
        </button>

        <div className="w-full max-w-6xl h-full md:h-[85vh] flex flex-col md:flex-row overflow-hidden bg-gallery-surface shadow-2xl relative">
          {/* Image */}
          <div className="w-full md:w-3/5 h-[50vh] md:h-full bg-gray-100 flex items-center justify-center p-8">
            <img 
              src={artwork.image} 
              alt={title} 
              className="max-w-full max-h-full object-contain shadow-lg"
            />
          </div>

          {/* Details */}
          <div className="w-full md:w-2/5 p-8 md:p-12 flex flex-col justify-center overflow-y-auto hide-scrollbar">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <h2 className="text-3xl md:text-5xl font-serif mb-4 leading-tight">{title}</h2>
              <p className="text-gallery-muted mb-8 text-lg">{description}</p>
              
              <div className="space-y-4 mb-12 border-y border-gallery-border py-6">
                <div className="flex justify-between text-sm">
                  <span className="text-gallery-muted uppercase tracking-widest">{t('artwork.medium')}</span>
                  <span className="text-right">{artwork.technique}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gallery-muted uppercase tracking-widest">{t('artwork.size')}</span>
                  <span className="text-right">{artwork.size}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gallery-muted uppercase tracking-widest">{t('artwork.price')}</span>
                  <span className="font-serif text-xl">€{artwork.price.toLocaleString()}</span>
                </div>
              </div>

              {artwork.available ? (
                <button 
                  onClick={handleAction}
                  className="w-full py-4 uppercase tracking-widest text-sm bg-gallery-text text-white hover:bg-gallery-accent transition-all duration-300"
                >
                  {artwork.stripe_payment_link ? (lang === 'en' ? 'BUY NOW' : 'COMPRAR') : (lang === 'en' ? 'CONTACT' : 'CONTACTAR')}
                </button>
              ) : (
                <div className="w-full py-4 text-center uppercase tracking-widest text-sm border border-gallery-muted text-gallery-muted">
                  {t('artwork.soldOut')}
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ArtworkModal;